<template>
  <user-feature component-name="WebCallBackorder">
    <div
      class="scrollable-list mr-2 my-2 phone:m-2 mx-2"
      :class="{'pr-2 flex items-center justify-center': isModal}"
    >
      <component
        :is="isModal ? WuiModal : 'section'"
        v-click-outside="onOutsideClick"
        class="flex flex-col"
        :class="isModal
          ? '!w-[456px] phone:!w-[360px] phone:!max-h-[600px]'
          : 'overflow-auto grow w-full h-full rounded-16 bg-elev-modal-bg'"
        v-bind="isModal ? { title: modalTitle, size: 40 } : {}"
        @close="() => close && close()"
      >
        <header
          v-if="!isModal"
          class="p-2 flex justify-between place-items-center border-b border-bor-nav tablet:w-full"
        >
          <div class="h-10 mr-5 p-2 truncate text-heading-sb-16-22 text-fg-base-fg3">
            {{ t('mainAside.webCallBackorder') }} <span v-if="contactNumber">{{ contactNumber }}</span>
          </div>
        </header>
        <div class="relative flex min-h-0 flex-1 flex-col @container grow phone:m-1">
          <section class="flex min-h-0 grow flex-col">
            <h4 class="sr-only select-none">
              Callback form
            </h4>
            <form
              class="flex min-h-0 flex-col tablet:p-4 phone:p-2"
              @submit.prevent="onSubmit"
            >
              <div class="min-h-0 flex-1 overflow-auto p-6">
                <wui-select
                  v-model="timeType"
                  :items="timeTypeItems"
                  :size="40"
                />
                <div
                  v-show="timeType === 'date'"
                  class="my-4"
                  data-test="date"
                >
                  <div class="grid @phone:grid-cols-2 grid-cols-1 flex-wrap items-center gap-2 mb-2">
                    <span class="text-1316 text-nowrap tablet:text-wrap">{{ t('calendar.dataCallBack') }}</span>
                    <wui-input
                      v-model="dateModel"
                      type="date"
                      append-icon="calendarM"
                      :placeholder="$t('common.date')"
                      :size="40"
                    />
                  </div>
                  <div class="grid @phone:grid-cols-2 grid-cols-1 flex-wrap items-center gap-2">
                    <span class="text-1316 text-nowrap tablet:text-wrap">{{ t('calendar.timeCallBack') }}</span>
                    <wui-input
                      v-model="time"
                      type="time"
                      append-icon="clockM"
                      :placeholder="$t('common.time')"
                      :size="40"
                    />
                  </div>
                </div>
                <div
                  class="my-6 grid @phone:grid-cols-2 grid-cols-1 flex-wrap items-center gap-2"
                  data-test="callback-number"
                >
                  <span class="text-1316 text-nowrap tablet:text-wrap">
                    {{ $t('mainAside.callbackNumber') }}
                  </span>
                  <wui-input
                    v-model="callbackNumber"
                    :error="!isPhoneNumberValid(callbackNumber)"
                    required
                    inputmode="tel"
                    :size="40"
                  />
                </div>
                <div class="mb-2 grid @phone:grid-cols-[2fr_1fr] grid-cols-1 flex-wrap items-center gap-2">
                  <span class="text-1316 text-nowrap tablet:text-wrap">
                    {{ $t('mainAside.attemptsCount') }}
                  </span>
                  <wui-select
                    v-model="attemptCount"
                    :items="attemptCountItems"
                    :size="40"
                  />
                </div>
                <div class="mb-2 grid @phone:grid-cols-[2fr_1fr] grid-cols-1 flex-wrap items-center gap-2">
                  <span class="text-1316 text-nowrap tablet:text-wrap">
                    {{ $t('mainAside.attemptsInterval') }}
                  </span>
                  <wui-select
                    v-model="attemptInterval"
                    :items="attemptIntervalItems"
                    :size="40"
                  />
                </div>
                <div
                  v-if="isPinCodeSet"
                  class="h-10 mb-2 flex items-center gap-4"
                >
                  <span class="text-1316 grow">
                    {{ $t('mainAside.callbackAuth') }}
                  </span>
                  <wui-input-switch
                    v-model="needAuth"
                    small
                  />
                </div>
                <div
                  v-if="needAuth"
                  class="mb-2 grid @sm:grid-cols-[2fr_1fr] grid-cols-1  flex-wrap items-center gap-2"
                >
                  <span class="text-1316 text-nowrap lg:text-wrap">
                    {{ $t('mainAside.attemptsInputCount') }}
                  </span>
                  <wui-select
                    v-model="attemptInputCount"
                    :items="attemptInputCountItems"
                    :size="40"
                  />
                </div>

                <div
                  v-if="needAuth"
                  class="mb-6 grid @sm:grid-cols-[2fr_1fr] grid-cols-1 flex-wrap items-center gap-2"
                >
                  <span class="text-1316 text-nowrap lg:text-wrap">
                    {{ $t('mainAside.inputTimeout') }}
                  </span>
                  <wui-select
                    v-model="inputTimeout"
                    :items="inputTimeoutItems"
                    :size="40"
                  />
                </div>

                <div class="grid @sm:grid-cols-[2fr_1fr] grid-cols-1 flex-wrap items-center gap-2">
                  <span class="text-1316 text-nowrap lg:text-wrap">
                    {{ $t('mainAside.autoConnectionNumber') }}
                  </span>
                  <wui-input
                    v-model="autoConnectionNumber"
                    type="text"
                    :error="!isPhoneNumberValid(autoConnectionNumber)"
                    inputmode="tel"
                    :size="40"
                  />
                </div>
              </div>
              <div
                v-if="!isModal"
                class="p-4 border-t border-bor-nav flex flex-row justify-end gap-x-2"
                data-test="submit-button"
              >
                <wui-btn
                  :size="40"
                  variant="brand"
                  state="filled"
                  :disabled="isNotValidForm"
                  type="submit"
                  auto-loading
                >
                  {{ $t('common.create') }}
                </wui-btn>
              </div>
            </form>

            <div
              v-if="callbacks.length && !isModal"
              class="relative grow mr-1 mb-1"
            >
              <web-callback-order-list
                class="px-2 py-2"
                :list="callbacks"
                :is-loading="isLoading"
              />
            </div>
          </section>
        </div>
        <template
          v-if="isModal"
          #footer
        >
          <div
            class="flex gap-x-2"
            data-test="submit-button"
          >
            <wui-btn
              :size="40"
              variant="neut"
              state="alpha"
              @click="() => close && close()"
            >
              {{ $t('login.cancel') }}
            </wui-btn>
            <wui-btn
              :size="40"
              variant="brand"
              state="filled"
              :disabled="isNotValidForm"
              auto-loading
              @click="onSubmit"
            >
              {{ $t('common.create') }}
            </wui-btn>
          </div>
        </template>
      </component>
    </div>
  </user-feature>
</template>

<script lang="ts" setup>
import {
  WuiSelect,
  WuiInput,
  WuiInputSwitch,
  SelectItem,
  WuiBtn,
  WuiModal,
  useRenderNotifier,
  closeDialogKey,
  safeInject,
  ClickOutside as vClickOutside,
} from '@wui/common-library'
import dayjs from 'dayjs'
import { storeToRefs } from 'pinia'
import { ref, Ref, computed, ComputedRef, onBeforeMount } from 'vue'

import {
  useCallBack,
  checkTimeValidity,
  formatCallBackDatetime,
  attemptCountItems,
  attemptInputCountItems,
  attemptIntervalItems,
  inputTimeoutItems,
  WebCallbackOrderList,
} from '@/entities/webCallBackOrder'

import { UserFeature } from '@/shared/components/user-feature'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useUserData, UserFields } from '@/shared/composables/local-storage'
import { useLocalization } from '@/shared/composables/localization'
import { CallBack } from '@/shared/types/api'
import { isPhoneNumberValid } from '@/shared/utils/validators'

const props = defineProps<{
  contactNumber?: string
  isModal?: boolean
  date?: Date
  callback?: CallBack
}>()

const { t } = useLocalization()
const modalTitle = computed(() => {
  if (props.contactNumber) {
    return `${t('mainAside.webCallBackorder')} ${props.contactNumber}`
  }
  return t('mainAside.webCallBackorder')
})
const callBackStore = useCallBack()
const { loadAllCallBack, saveCallBack, deleteCallBack } = callBackStore
const { callBackLists, isLoading } = storeToRefs(callBackStore)

const callbacks = computed(() => [...callBackLists.value.values()])

type Time = 'now' | 'date'
const timeType: Ref<Time> = ref(props.date ? 'date' : 'now')

const timeTypeItems: ComputedRef<SelectItem[]> = computed(() => [{
  label: t('mainAside.now'),
  value: 'now',
}, {
  label: t('common.atSpecifiedTime'),
  value: 'date',
}])

const isNotValidForm = computed (() => {
  return !callbackNumber.value.length
    || !isPhoneNumberValid(callbackNumber.value)
    || !isPhoneNumberValid(autoConnectionNumber.value)
})

const { notifyWarning, notifyError, notifySuccess } = useRenderNotifier()

const dayJsDate = ref(props.date ? dayjs(props.date).tz().add(30, 'minutes') : dayjs().tz().add(30, 'minutes'))
const dateModel = ref(dayJsDate.value.format('YYYY-MM-DD'))
const time = ref(`${dayJsDate.value.format('HH:mm')}`)

const callbackNumber = ref(props.contactNumber ?? '')

const needAuth: Ref<boolean> = ref(false)
const { getField } = useUserData()
const isPinCodeSet = computed(() => Boolean(getField<string>(UserFields.PIN_CODE)))

const { currentUser } = useCurrentUser()
const currentUserNumber = computed(() => currentUser.value?.internalNumber ?? '')

const autoConnectionNumber = ref(currentUserNumber.value)

const attemptCount = ref('5')
const attemptInterval = ref('1')
const attemptInputCount = ref('5')
const inputTimeout = ref('20')

const model = computed<Omit<CallBack, 'guid'>>(() => {
  const callBack: Omit<CallBack, 'guid'> = {
    src: callbackNumber.value,
    attemptCount: +attemptCount.value,
    attemptInterval: +attemptInterval.value,
    attemptInputCount: +attemptInputCount.value,
    attemptInputTimeout: +inputTimeout.value,
    needSrcAuthentication: isPinCodeSet.value ? needAuth.value : false,
  }

  if (timeType.value === 'date') {
    callBack.datetime = formatCallBackDatetime(dateModel.value, time.value)
  }

  if (autoConnectionNumber.value) {
    callBack.dst = autoConnectionNumber.value
  }

  return callBack
})

const getCurrentDateTime = () => {
  const current = dayjs().tz().add(1, 'minute')
  return {
    date: current.format('YYYY-MM-DD'),
    time: current.format('HH:mm'),
  }
}

let close: (() => void) | undefined = undefined
if (props.isModal) {
  close = safeInject<() => void>(closeDialogKey)
}

const onSubmit = async () => {
  if (timeType.value === 'now') {
    const now = getCurrentDateTime()
    dateModel.value = now.date
    time.value = now.time
  }

  if (!callbackNumber.value.trim()) {
    notifyWarning(t('common.required'))
    return
  }

  if (!checkTimeValidity(dateModel.value, time.value)) {
    notifyWarning(t('common.notValidTime'))
    return
  }

  try {
    await saveCallBack(model.value)
    callbackNumber.value = props.contactNumber ?? ''
    autoConnectionNumber.value = currentUserNumber.value
    if (props.callback) {
      await deleteCallBack(props.callback.guid ?? '')
    }

    notifySuccess(t('common.callbackCreated'))

    if (close) {
      close()
    }
  } catch (e) {
    const errorMessage = e instanceof Error ? e.message : t('common.unknownError')
    notifyError(errorMessage)
    console.error(e)
  }
}

onBeforeMount(() => {
  loadAllCallBack()
})

const onOutsideClick = () => {
  if (close && props.isModal) {
    close()
  }
}

</script>
